import {
  getTherapy,
  getTherapyType,
  IAudit,
  IIntegratedCareReturnDelivery,
  IOrder,
  IPatient,
  ISingleOrder,
  ITemplate,
} from '@alberta/konexi-shared';
import { Injectable } from '@angular/core';
import { observe } from '@common/tracking';
import { TranslateService } from '@ngx-translate/core';
import { cloneDeep, get as lodashGet } from 'lodash';
import { map } from 'rxjs/operators';
import { AuditViewModel } from 'src/app/business/swodoc/view-models/audit-view-model';
import { map as mapperMap } from 'src/app/common/mapper/mapper';
import { AuditDB, SingleOrderDB, UsersDB } from 'src/app/common/repository/databases';
import { UserDto } from 'src/app/shared/models/user-management/user-dto.model';
import { QueryService } from 'src/app/shared/services/query/query.service';
import { AuditComponentModel } from 'src/app/swodoc/_models/audit-component-model';
import type { IPrepareAuditMetaDataInterface } from 'src/app/swodoc/services/swodoc-create.service';
import { BodyPart } from '../../models/body-part.model';
import { AuditDto } from '../../models/swodoc/audit-dto.model';
import { AuthService } from '../auth.service';
import { BodyPartService } from '../body-part.service';
import { GeneralEntityService } from '../general-entity.service';
import { PatientService } from '../patient.service';
import { AuditBaseService } from './audit-base-service';
@Injectable({ providedIn: 'root' })
export class AuditService extends AuditBaseService {
  constructor(
    private _itemsProvider: GeneralEntityService,
    private _bodyPartService: BodyPartService,
    private auditModel: AuditComponentModel,
    private queryService: QueryService,
    private _patientService: PatientService,
    private _translate: TranslateService,
    private _auditComponentModel: AuditComponentModel,
    _auth: AuthService
  ) {
    super(_auth);
  }

  public async getNotArchivedAuditsForTherapyProgress(params: {
    patientId: string;
    therapyId: number;
    therapyTypeId: number;
    woundLocation: String;
    completed: boolean;
  }): Promise<IAudit[]> {
    const audits = await this.queryService.search<IAudit>(
      {
        query: `patientId:${params.patientId}`,
      },
      AuditDB
    );
    const matchingAudits = audits.filter((audit: IAudit) => {
      return (
        audit.therapyId === params.therapyId &&
        audit.therapyTypeId === params.therapyTypeId &&
        audit.woundLocation === params.woundLocation &&
        audit.completed === params.completed &&
        !audit.archived && // archived:true in query funktioniert mobil nicht - so ist es sehr ineffeizient.
        !audit.isWoundLocationHealed
      );
    });
    return matchingAudits;
  }

  public async getUnarchivedPatientAudits(patientId: string): Promise<IAudit[]> {
    const audits = await this.queryService.search<IAudit>(
      {
        query: `patientId:${patientId}`,
      },
      AuditDB
    );
    const matchingAudits = audits.filter((audit: IAudit) => {
      return audit.archived === false;
    });
    return matchingAudits;
  }

  public async createPatientAudit(
    template: ITemplate,
    patientId: string,
    regionId: string,
    prefillData?: any[]
  ): Promise<IAudit> {
    const audit = await this.createAudit(template, prefillData);
    audit.patientId = patientId;
    audit.regionId = regionId;
    return audit;
  }

  public async copyAudit(template: ITemplate, audit: AuditDto): Promise<IAudit> {
    const prefillData = await this.getPrefillData(
      template._id,
      audit.patientId,
      {
        patient: null,
        selectedTherapy: audit.therapyId,
        selectedTherapyType: audit.therapyTypeId,
        selectedWoundLocation: BodyPart[audit.woundLocation],
        additionalInfo: audit.additionalInfo,
      },
      audit.createdBy
    );
    const newAudit = await this.createPatientAudit(template, audit.patientId, audit.regionId, prefillData);
    newAudit.type = audit.type;
    newAudit.therapyId = audit.therapyId;
    newAudit.therapyTypeId = audit.therapyTypeId;
    newAudit.woundLocation = audit.woundLocation;
    newAudit.additionalInfo = audit.additionalInfo;
    newAudit.regionId = audit.regionId;
    newAudit.auditNodes.forEach((section, sectionIndex) => {
      section.children.forEach((node, nodeIndex) => {
        if (
          node.nodeType_Id !== 'image' &&
          node.nodeType_Id !== 'signature' &&
          node.nodeType_Id !== 'signotec_signature' &&
          node.nodeType_Id !== 'formula' &&
          node.nodeType_Id !== 'document_scan_ios' &&
          !node.isSystemNode &&
          !node.systemNode
        ) {
          let auditNodeToCopy = audit.auditNodes[sectionIndex].children[nodeIndex];

          if (
            !auditNodeToCopy ||
            (auditNodeToCopy &&
              auditNodeToCopy.templateNodeId &&
              node.templateNodeId &&
              auditNodeToCopy.templateNodeId !== node.templateNodeId)
          ) {
            auditNodeToCopy = audit.findNodeByTemplateNodeId(node.templateNodeId);
          }

          if (!auditNodeToCopy) {
            return;
          }
          if (auditNodeToCopy.responses && auditNodeToCopy.responses.length > 0) {
            node.responses = cloneDeep(auditNodeToCopy.responses);
          }
          if (auditNodeToCopy.selectables && auditNodeToCopy.selectables.length > 0) {
            node.selectables = cloneDeep(auditNodeToCopy.selectables);
          }
          if (auditNodeToCopy.nodeType_Id === 'picture') {
            node.responses = [];
          }
        }
      });
    });
    return newAudit;
  }

  /** @param overrideWoundLocationText optional parameter used for medicalOrderRequests(Verordnung) in Services. They can have multiple locations*/
  public async getPrefillData(
    templateId: string,
    patientId: string,
    metaData: IPrepareAuditMetaDataInterface,
    userId: string,
    returnDelivery?: IIntegratedCareReturnDelivery,
    overrideWoundLocationText?: string,
    singleOrder?: ISingleOrder
  ): Promise<any[]> {
    return this.auditModel
      .getMatch(templateId)
      .pipe(
        map(async (match: any) => {
          if (!match || !match.fields) {
            return;
          }
          const patient = await this._patientService.find(patientId);
          return await Promise.all(
            match.fields.map(field =>
              this.mapFieldValue(
                field,
                patient,
                metaData,
                userId,
                returnDelivery,
                overrideWoundLocationText,
                singleOrder
              )
            )
          );
        })
      )
      .toPromise();
  }
  /** @param overrideWoundLocationText optional parameter used for medicalOrderRequests(Verordnung) in Services. They can have multiple locations*/
  private async mapFieldValue(
    field: any,
    patient: IPatient,
    metaData: IPrepareAuditMetaDataInterface,
    userId: string,
    returnDelivery?: IIntegratedCareReturnDelivery,
    overrideWoundLocationText?: string,
    singleOrder?: ISingleOrder
  ): Promise<any> {
    if (!field.alberta.entity || field.alberta.entity === 'patient') {
      if (!patient) {
        return { _id: field.swodoc, value: undefined };
      }

      if (field.alberta.restriction && field.alberta.subPath) {
        return await this.formatValueByRestriction(field, patient);
      }

      if (!field.alberta.modelName) {
        return this.formatValue(field.swodoc, patient, field.alberta.path, field.alberta.lookup);
      }

      const key = lodashGet(patient, field.alberta.path);
      if (!key) {
        return { _id: field.swodoc, value: undefined };
      }

      this._itemsProvider.type = field.alberta.modelName as GeneralEntityService['type'];
      const item = await this._itemsProvider.find(key);

      return !item
        ? { _id: field.swodoc, value: undefined }
        : this.formatValue(field.swodoc, item, field.alberta.modelPath, field.alberta.lookup);
    }
    if (field.alberta.entity === 'audit' && field.alberta.path === 'woundLocation') {
      const woundLocationId = metaData.selectedWoundLocation ? metaData.selectedWoundLocation.areaId.toString() : null;
      if (woundLocationId) {
        const value = `${this._bodyPartService.getFullBodyPartName(Number.parseInt(woundLocationId, 10))}`;
        return this.formatReturnValue(value, field.swodoc);
      }
    }
    if (field.alberta.entity === 'audit' && field.alberta.path === 'therapy' && metaData.selectedTherapy) {
      const therapy = getTherapy(metaData.selectedTherapy)?.displayName;
      return this.formatReturnValue(therapy, field.swodoc);
    }
    if (field.alberta.entity === 'audit' && field.alberta.path === 'therapyType' && metaData.selectedTherapyType) {
      const therapyType = getTherapyType(metaData.selectedTherapy, metaData.selectedTherapyType)?.displayName;
      return this.formatReturnValue(therapyType, field.swodoc);
    }
    if (field.alberta.entity === 'audit' && field.alberta.path === 'additionalInfo' && metaData.additionalInfo) {
      return this.formatReturnValue(metaData.additionalInfo, field.swodoc);
    }
    if (field.alberta.entity === 'user' && userId) {
      const user = await this.getUser(userId);
      const value = lodashGet(user, field.alberta.path);
      return this.formatReturnValue(value, field.swodoc);
    }
    if (field.alberta.entity === 'prescriptionRequest') {
      return this.formatPrescriptionRequestValue(field, metaData, overrideWoundLocationText);
    }
    if (field.alberta.entity === 'returnDelivery' && returnDelivery) {
      return this.formatReturnDeliveryValue(field, returnDelivery);
    }
    if (field.alberta.entity === 'singleOrder' && singleOrder) {
      return this.formatSingleOrderValue(field, singleOrder);
    }
    if (field.alberta.entity === 'fieldNurse') {
      const userId = lodashGet(patient, field.alberta.path);
      if (!userId) {
        return { _id: field.swodoc, value: undefined };
      }
      const user = await this.getUser(userId);
      const value = lodashGet(user, field.alberta.modelPath);
      if (!value) {
        return { _id: field.swodoc, value: undefined };
      }
      return this.formatReturnValue(value, field.swodoc);
    }

    return { _id: field.swodoc, value: undefined };
  }

  private async formatSingleOrderValue(field: any, singleOrder: ISingleOrder): Promise<any> {
    switch (field.alberta.path) {
      case 'startDate':
        return this.formatReturnValue(singleOrder.startDate, field.swodoc);
      case 'endDate':
        return this.formatReturnValue(singleOrder.endDate, field.swodoc);
      case 'therapy':
        const therapy = getTherapy(singleOrder.therapyId)?.displayName;
        return this.formatReturnValue(therapy, field.swodoc);
      case 'therapyTypeId':
        const therapyType = getTherapyType(singleOrder.therapyId, singleOrder.therapyTypeId)?.displayName;
        return this.formatReturnValue(therapyType, field.swodoc);
      case 'deliveryDate':
        return this.formatReturnValue(singleOrder.deliveryDate, field.swodoc);
      case 'accountingMonth':
        if (singleOrder.accountingMonth && Array.isArray(singleOrder.accountingMonth)) {
          const monthNames = singleOrder.accountingMonth.map(monthNum => {
            return this._translate.instant(`months.${monthNum}`);
          });
          return this.formatReturnValue(monthNames.join(', '), field.swodoc);
        }
        return this.formatReturnValue(singleOrder.accountingMonth, field.swodoc);
      case 'deliveryAddress.address':
        return this.formatReturnValue(singleOrder.deliveryAddress.address, field.swodoc);
      case 'deliveryAddress.city':
        return this.formatReturnValue(singleOrder.deliveryAddress.city, field.swodoc);
      case 'deliveryAddress.postalCode':
        return this.formatReturnValue(singleOrder.deliveryAddress.postalCode, field.swodoc);
      case 'deliveryInformation':
        return this.formatReturnValue(singleOrder.deliveryInformation, field.swodoc);
      case 'annotation':
        return this.formatReturnValue(singleOrder.annotation, field.swodoc);
      case 'deliveryNote':
        return this.formatReturnValue(singleOrder.deliveryNote, field.swodoc);
      case 'deliveryOption':
        return this.formatReturnValue(singleOrder.deliveryOption, field.swodoc);
    }
  }

  private async formatReturnDeliveryValue(field: any, returnDelivery: IIntegratedCareReturnDelivery): Promise<any> {
    switch (field.alberta.path) {
      case 'returnDeliveryDate':
        return this.formatReturnValue(returnDelivery.returnDeliveryDate, field.swodoc);
      case 'countOfPackages':
        return this.formatReturnValue(returnDelivery.countOfPackages, field.swodoc);
      case 'reason':
        return this.formatReturnValue(returnDelivery.reason, field.swodoc);
      case 'returnDeliveryAddress.city':
        return this.formatReturnValue(returnDelivery.returnDeliveryAddress.city, field.swodoc);
      case 'returnDeliveryAddress.postalCode':
        return this.formatReturnValue(returnDelivery.returnDeliveryAddress.postalCode, field.swodoc);
      case 'returnDeliveryAddress.address':
        return this.formatReturnValue(returnDelivery.returnDeliveryAddress.address, field.swodoc);
      case 'orderIds':
        let returnString = '';
        if (returnDelivery?.orderIds?.length > 0) {
          let formattedDates: string[] = [];
          await this.queryService.getByIds<IOrder>(returnDelivery.orderIds, SingleOrderDB).then(orders => {
            orders.forEach(order => {
              let deliverydate;
              try {
                // ensure we have a date object
                if (order.deliveryDate instanceof Date) {
                  deliverydate = order.deliveryDate;
                } else {
                  deliverydate = new Date(order.deliveryDate);
                }
                // format the date as dd.mm.yyyy
                const day = deliverydate.getDate().toString().padStart(2, '0');
                const month = (deliverydate.getMonth() + 1).toString().padStart(2, '0');
                const year = deliverydate.getFullYear();
                const formattedDate = `${day}.${month}.${year}`;
                formattedDates.push(formattedDate);
              } catch (error) {
                window.logger.error('error converting delivery date', error);
                formattedDates.push('Fehlerhaftes Lieferdatum');
              }
            });
          });
          if (formattedDates.length > 0) {
            returnString = formattedDates.join(', ');
          }
          return this.formatReturnValue(returnString, field.swodoc);
        } else return { _id: field.swodoc, value: undefined };
    }
  }

  private formatValue(_id: string, item: any, path: string, lookup?: string[]) {
    let value = lodashGet(item, path);
    if (value instanceof Date) {
      value = value.toISOString();
    }
    if (Array.isArray(value)) {
      value = value.join(', ');
    }
    if (lookup) {
      value = lookup[value];
    }
    return { _id, value };
  }

  private async formatValueByRestriction(field: any, patient: IPatient): Promise<{ _id: string; value: string }> {
    const patientData = lodashGet(patient, field.alberta.path);
    if (patientData && Array.isArray(patientData)) {
      const itemByRestriction = patientData.find(item => {
        const itemData = lodashGet(item, field.alberta.restriction.path);
        if (itemData === field.alberta.restriction.equals) {
          return item;
        }
      });
      if (itemByRestriction) {
        let value = lodashGet(itemByRestriction, field.alberta.subPath);
        if (value !== null) {
          if (field.alberta.lookup) {
            value = field.alberta.lookup[value];
          } else if (field.alberta.modelName) {
            this._itemsProvider.type = field.alberta.modelName as GeneralEntityService['type'];
            const generalEntityItem = await this._itemsProvider.find(value);
            value = lodashGet(generalEntityItem, field.alberta.modelPath);
          }
          return { _id: field.swodoc, value };
        }
      }
    }
    return { _id: field.swodoc, value: undefined };
  }
  /** @param overrideWoundLocationText optional parameter used for medicalOrderRequests(Verordnung) in Services. They can have multiple locations*/
  private formatPrescriptionRequestValue(
    field: any,
    metaData: IPrepareAuditMetaDataInterface,
    overrideWoundLocationText?: string
  ): { _id: string; value: string } {
    switch (field.alberta.path) {
      case 'therapyId':
        if (metaData?.selectedTherapy != null) {
          const therapy = getTherapy(metaData.selectedTherapy)?.displayName;
          return this.formatReturnValue(therapy, field.swodoc);
        }
        return { _id: field.swodoc, value: undefined };
      case 'therapyTypeId':
        if (metaData?.selectedTherapyType != null) {
          const therapyType = getTherapyType(metaData.selectedTherapy, metaData.selectedTherapyType)?.displayName;
          return this.formatReturnValue(therapyType, field.swodoc);
        }
        return { _id: field.swodoc, value: undefined };
      case 'woundLocation':
        // 999 is placeholder for multiple locations
        if (metaData?.selectedWoundLocation?.areaId === 999) {
          if (overrideWoundLocationText) {
            return this.formatReturnValue(overrideWoundLocationText, field.swodoc);
          } else {
            return { _id: field.swodoc, value: undefined };
          }
        }
        if (metaData?.selectedWoundLocation?.areaId) {
          const woundLocationId = metaData.selectedWoundLocation
            ? metaData.selectedWoundLocation.areaId.toString()
            : null;
          if (woundLocationId) {
            const value = `${this._bodyPartService.getFullBodyPartName(Number.parseInt(woundLocationId, 10))}`;
            return this.formatReturnValue(value, field.swodoc);
          }
        }

        return { _id: field.swodoc, value: undefined };
      default:
        return { _id: field.swodoc, value: undefined };
    }
  }

  private formatReturnValue(value: any, swodocField: any): { _id: string; value: string } {
    return (value ? { _id: swodocField, value } : { _id: swodocField, value: undefined }) as any;
  }

  private async getUser(userId: string): Promise<UserDto> {
    return this.queryService.get<UserDto>(userId, UsersDB);
  }

  public async setAuditHealed(auditId: string) {
    const audit = await this.queryService.get<IAudit>(auditId, AuditDB);
    const auditViewModel = observe(mapperMap(audit, new AuditViewModel())) as IAudit;
    auditViewModel.isWoundLocationHealed = true;
    await this._auditComponentModel.edit(auditViewModel, mapperMap(auditViewModel, new AuditDto()));
  }

  public async unSetAuditHealed(auditId: string) {
    const audit = await this.queryService.get<IAudit>(auditId, AuditDB);
    const auditViewModel = observe(mapperMap(audit, new AuditViewModel())) as IAudit;
    auditViewModel.isWoundLocationHealed = false;
    await this._auditComponentModel.edit(auditViewModel, mapperMap(auditViewModel, new AuditDto()));
  }
}
